import { Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import type { Disposable } from '@gitlab-org/disposable';
import {
  OfficialMarketplaceSeeder,
  PluginAutoUpdater,
  PluginCacheSweeper,
  type AutoUpdatedPlugin,
} from '@gitlab-org/duo-plugin-marketplace/node';

const PLUGIN_STARTUP_DELAY_MS = 5000;

/**
 * Runs background plugin maintenance shortly after TUI startup: cache sweep,
 * official marketplace seeding, and auto-update of opted-in plugins. Deferred
 * so it does not compete with session startup I/O. Surfaces a TUI info message
 * when any plugin was updated.
 */
@Service({
  dependencies: [PluginCacheSweeper, PluginAutoUpdater, OfficialMarketplaceSeeder, Logger],
  lifetime: ServiceLifetime.Singleton,
})
export class PluginStartupService implements Disposable {
  #pluginCacheSweeper: PluginCacheSweeper;

  #pluginAutoUpdater: PluginAutoUpdater;

  #officialMarketplaceSeeder: OfficialMarketplaceSeeder;

  #logger: Logger;

  #timeoutHandle?: NodeJS.Timeout;

  #resolveDelay?: () => void;

  #disposed = false;

  #startPromise?: Promise<void>;

  constructor(
    pluginCacheSweeper: PluginCacheSweeper,
    pluginAutoUpdater: PluginAutoUpdater,
    officialMarketplaceSeeder: OfficialMarketplaceSeeder,
    logger: Logger,
  ) {
    this.#pluginCacheSweeper = pluginCacheSweeper;
    this.#pluginAutoUpdater = pluginAutoUpdater;
    this.#officialMarketplaceSeeder = officialMarketplaceSeeder;
    this.#logger = withPrefix(logger, '[PluginStartupService]');
  }

  start(notify: (message: string) => void): Promise<void> {
    this.#startPromise ??= this.#runStart(notify);
    return this.#startPromise;
  }

  async #runStart(notify: (message: string) => void): Promise<void> {
    await new Promise<void>((resolve) => {
      this.#resolveDelay = resolve;
      this.#timeoutHandle = setTimeout(resolve, PLUGIN_STARTUP_DELAY_MS);
    });
    this.#timeoutHandle = undefined;
    this.#resolveDelay = undefined;

    if (this.#disposed) {
      return;
    }

    try {
      await this.#pluginCacheSweeper.sweep();
    } catch (e) {
      this.#logger.warn(
        'Plugin cache cleanup sweep failed',
        e instanceof Error ? e : new Error(String(e)),
      );
    }

    try {
      const result = await this.#pluginAutoUpdater.run();
      if (result.updated.length > 0) {
        notify(this.#formatMessage(result.updated));
      }
    } catch (e) {
      this.#logger.warn('Plugin auto-update failed', e instanceof Error ? e : new Error(String(e)));
    }

    try {
      await this.#officialMarketplaceSeeder.seed();
    } catch (e) {
      this.#logger.warn(
        'Plugin marketplace seeding failed',
        e instanceof Error ? e : new Error(String(e)),
      );
    }
  }

  dispose(): void {
    this.#disposed = true;
    if (this.#timeoutHandle) {
      clearTimeout(this.#timeoutHandle);
      this.#timeoutHandle = undefined;
    }
    this.#resolveDelay?.();
    this.#resolveDelay = undefined;
  }

  #formatMessage(updated: AutoUpdatedPlugin[]): string {
    if (updated.length === 1) {
      const [plugin] = updated;
      return `Plugin ${plugin.id} updated to ${plugin.version}. Restart to load the new version.`;
    }
    const list = updated.map((plugin) => `${plugin.id} (${plugin.version})`).join(', ');
    return `Updated ${updated.length} plugins: ${list}. Restart to load the new versions.`;
  }
}
